import { el } from '../panels.js';
import { phoneIconSvg, type PhoneIconName } from './icons.js';
import { openSheet, type SheetHandle } from './sheet.js';
import type { PhoneEnv } from './types-phone.js';

/**
 * The long-press menu: Zazzle's desktop right-click menu, as a sheet.
 *
 * It is opened by the shell's own press detector (and by a real right-click,
 * so it can be driven with a mouse in the emulated phone), never by the
 * canvas — the canvas's own long press opens a tool, which is the gesture a
 * desktop double-click performs.
 */
export function openContextSheet(env: PhoneEnv): SheetHandle {
  const ctx = env.ctx;
  const t = env.texts;
  const layer = ctx.selected();

  interface Row {
    id: string;
    label: string;
    icon: PhoneIconName;
    disabled?: boolean;
    run(): void;
  }

  const rows: Row[] = [
    { id: 'copy', label: t.copy, icon: 'copy', disabled: !layer, run: () => ctx.copySelection() },
    { id: 'paste', label: t.paste, icon: 'paste', run: () => ctx.pasteSelection() },
    {
      id: 'delete',
      label: t.delete,
      icon: 'trash',
      disabled: !layer,
      run: () => {
        if (!layer) return;
        ctx.selection.delete(layer.id);
        ctx.dispatch({ type: 'remove', id: layer.id });
      },
    },
    {
      id: 'forward',
      label: t.forward,
      icon: 'up',
      disabled: !layer,
      run: () => layer && ctx.dispatch({ type: 'raise', id: layer.id }),
    },
    {
      id: 'backward',
      label: t.backward,
      icon: 'down',
      disabled: !layer,
      run: () => layer && ctx.dispatch({ type: 'lower', id: layer.id }),
    },
    { id: 'layers', label: t.layers, icon: 'layers', run: () => env.openAdd('layers') },
  ];

  return openSheet({
    root: env.root,
    title: t.contextTitle,
    closeLabel: t.close,
    build: (body, sheet) => {
      const list = el('div', 'pzd-ph-list');
      for (const row of rows) {
        const button = el('button', `pzd-ph-list-btn is-${row.id}`);
        button.type = 'button';
        button.dataset.action = row.id;
        button.setAttribute('aria-label', row.label);
        const icon = el('span');
        icon.innerHTML = phoneIconSvg(row.icon, 18);
        button.append(icon, el('span', undefined, row.label));
        if (row.disabled) button.disabled = true;
        button.addEventListener('click', () => {
          // "Slojevi" opens another sheet, so it closes this one itself.
          if (row.id !== 'layers') sheet.close();
          row.run();
        });
        list.append(button);
      }
      body.append(list);
    },
  });
}
